import { timelineHeight } from "@/app/activity/components/timeline/activityTimeline/constants"
import { useGetTimePerWidth } from "@/app/activity/components/timeline/activityTimeline/hooks"
import TimelineItemBox from "@/app/activity/components/timeline/activityTimeline/timelineItemBox/TimelineItemBox"
import TimelineTick from "@/app/activity/components/timeline/activityTimeline/timelineTick/TimelineTick"
import {
  Magnification,
  RangeKey,
  TimelineItemBoxProps,
} from "@/app/activity/components/timeline/activityTimeline/type"
import { createActivityCommonStore, createSessionStore } from "@/app/activity/store"

import { colors } from "@/components/colors"

import { pointer } from "d3-selection"

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react"

import moment from "moment"
import { getTimezoneOffset } from "@/utils/format"
import { cloneDeep } from "lodash"
import { Session } from "@/types/session"

import classNames from "classnames"
import SessionGuideLine from "@/app/activity/components/guideline/sessionGuideline/SessionGuideline"
import { SessionTimelineProps } from "@/app/activity/components/timeline/sessionTimeline/type"

export default function SessionTimeline({
  rangeKey = RangeKey.ACTIVITY,
  height,
  onBoxClick,
}: SessionTimelineProps) {
  const ref = useRef<SVGSVGElement>(null)
  const svgContRef = useRef<HTMLDivElement>(null)
  const interactiveRectRef = useRef<SVGRectElement>(null)
  const initialWidthRef = useRef(0) // resizeHandler에서 stale closure 방지용

  // onDrag 관련 이벤트는 addEventListener로 등록하기 때문에 ref.current로 내용 변경을 추적 해야 한다.

  const [originalWidth, setOriginalWidth] = useState(1)
  const [initialWidth, setInitialWidth] = useState(0) // 초기 컨테이너 너비 (고정값)
  const [width, setWidth] = useState(1)
  const [isInit, setIsInit] = useState(true)

  const [mouseGuidelineX, setMouseGuidelineX] = useState(0)
  const [mouseGuidelineDate, setMouseGuidelineDate] = useState<Date>()
  const [currentItem, setCurrentItem] = useState<Session>()

  const [prevMagnification, setPrevMagnification] = useState<number>(Magnification.INIT)
  // 스토어 데이터가 직접 변경되는 것을 방지하기 위해 복사본을 사용

  const {
    selectedSession,
    setSessionList,
    sessionList,
    temporarySession,

    setTemporarySession,
  } = createSessionStore((state) => ({
    selectedSession: state.session,
    setSession: state.setSession,
    setSessionList: state.setSessions,
    sessionList: state.sessions,
    temporarySession: state.temporarySession,
    setTemporarySession: state.setTemporarySession,
  }))

  const {
    setTimePerWidth,
    setTimelineWidth,
    magnification,

    remainRightArea = 0,
    setRemainRightArea,
    setSessionMouseGuideLine,
    sessionMouseGuideLine,
  } = createActivityCommonStore((state) => ({
    selectedDate: state.selectedDate,
    setSelectedDate: state.setSelectedDate,
    setTimePerWidth: state.setTimePerWidth,
    setTimelineWidth: state.setTimelineWidth,
    timelineWidth: state.timelineWidth,

    magnification: state.magnification,
    remainRightArea: state.remainingArea,
    setRemainRightArea: state.setRemainingArea,
    setSessionMouseGuideLine: state.setSessionMouseGuideLine,
    sessionMouseGuideLine: state.sessionMouseGuideLine,
  }))

  const timePerWidth = useGetTimePerWidth({
    dateStart: new Date(currentItem?.dateStart as string),
    dateFinish: new Date(currentItem?.dateFinish as string),
    chartWidth: width,
  })

  const timezoneOffset = getTimezoneOffset()

  const tickStartDate = useMemo(() => {
    if (remainRightArea && currentItem?.dateStart) {
      return new Date(
        new Date(currentItem?.dateStart).valueOf() - (remainRightArea / 2) * timePerWidth,
      )
    }

    if (currentItem?.dateStart) {
      return new Date(currentItem?.dateStart.valueOf())
    }
  }, [remainRightArea, currentItem?.dateStart])

  const tickFinishDate = useMemo(() => {
    if (remainRightArea && currentItem?.dateFinish) {
      return new Date(
        new Date(currentItem?.dateFinish).valueOf() + (remainRightArea / 2) * timePerWidth,
      )
    }

    if (currentItem?.dateFinish) {
      return new Date(currentItem?.dateFinish.valueOf())
    }
  }, [remainRightArea, currentItem?.dateFinish])

  const resizeHandler = () => {
    if (svgContRef.current && initialWidthRef.current > 0) {
      const currentContainerWidth = svgContRef.current.clientWidth
      // 컨테이너가 초기 너비보다 커지면 기준 너비를 업데이트
      // 작아지면 아무것도 안 함 (SVG는 고정, 스크롤 생성)
      if (currentContainerWidth > initialWidthRef.current) {
        initialWidthRef.current = currentContainerWidth
        setInitialWidth(currentContainerWidth)
        setOriginalWidth(currentContainerWidth)
        setWidth(currentContainerWidth * (magnification || Magnification.INIT))
      }
    }
  }

  const onMouseEnter = (event: React.MouseEvent<SVGSVGElement | SVGLineElement, MouseEvent>) => {
    const [x] = pointer(event)

    setMouseGuidelineX(x)

    if (currentItem?.dateStart && currentItem?.dateFinish) {
      setMouseGuidelineDate(getGuideLineDate(x))
    }
  }

  const onMouseMove = (event: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
    const [x] = pointer(event)

    setMouseGuidelineX(x)

    if (currentItem?.dateStart && currentItem?.dateFinish) {
      setMouseGuidelineDate(getGuideLineDate(x))
    }
  }

  const getGuideLineDate = (position: number) => {
    const startDate = tickStartDate as Date
    const finishDate = tickFinishDate as Date

    const time =
      (position / Math.max(width, originalWidth)) * (finishDate.getTime() - startDate.getTime()) +
      startDate.getTime()

    return new Date(time)
  }

  const onKeyDown = function (e?: KeyboardEvent) {
    if (e?.code !== "Escape") {
      return
    }
  }

  const handleScrollEvent = (event: React.UIEvent<HTMLDivElement>) => {
    event.stopPropagation()
    event.preventDefault()
    const target = event.target as HTMLElement
    const scrollEle = document.querySelector(".simplebar-content-wrapper")

    if (scrollEle) {
      scrollEle.scrollLeft = target.scrollLeft
    }
  }

  useLayoutEffect(() => {
    let result = 0
    if ((magnification as Magnification) >= Magnification.INIT) {
      result = 0
    }

    result = Math.abs(Math.max(0, originalWidth - width))

    setRemainRightArea?.(result)
  }, [magnification, width, originalWidth])

  useEffect(() => {
    setSessionMouseGuideLine({
      x: mouseGuidelineX,
      date: mouseGuidelineDate as Date,
      isVisible: false,
    })
  }, [mouseGuidelineDate, mouseGuidelineX])

  // 초기 컨테이너 너비 저장 (한 번만)
  useEffect(() => {
    if (svgContRef.current && initialWidth === 0) {
      const containerWidth = svgContRef.current.clientWidth
      initialWidthRef.current = containerWidth // resizeHandler에서 사용
      setInitialWidth(containerWidth)
      setOriginalWidth(containerWidth)
      setWidth(containerWidth * (magnification || Magnification.INIT))
    }
  }, [svgContRef.current, initialWidth])

  useEffect(() => {
    window.addEventListener("keydown", onKeyDown)

    return () => {
      window.removeEventListener("keydown", onKeyDown)
    }
  }, [])

  useEffect(() => {
    setPrevMagnification(() => magnification || Magnification.INIT)
    setIsInit(false)
    window.addEventListener("resize", resizeHandler)

    return () => {
      window.removeEventListener("resize", resizeHandler)
    }
  }, [])

  useEffect(() => {
    if (isInit || initialWidth === 0) {
      return
    }

    setWidth(() => initialWidth * (magnification || Magnification.INIT))
    setPrevMagnification(magnification || Magnification.INIT)

    if ((magnification as number) < prevMagnification) {
      if (temporarySession) {
        setTemporarySession({
          ...(temporarySession as Session),
          width: (temporarySession as unknown as Record<string, number>).width / 2,
          startX: (temporarySession as unknown as Record<string, number>).startX / 2,
        } as unknown as Session)
      }

      if (currentItem) {
        setCurrentItem({
          ...(currentItem as Session),
          width: (currentItem as unknown as Record<string, number>).width / 2,
          startX: (currentItem as unknown as Record<string, number>).startX / 2,
        } as unknown as Session)
      }
    } else if ((magnification as number) > prevMagnification) {
      if (temporarySession) {
        setTemporarySession({
          ...(temporarySession as Session),
          width: (temporarySession as unknown as Record<string, number>).width * 2,
          startX: (temporarySession as unknown as Record<string, number>).startX * 2,
        } as unknown as Session)
      }

      setCurrentItem({
        ...(selectedSession as Session),
        width: (selectedSession as unknown as Record<string, number>).width * 2,
        startX: (selectedSession as unknown as Record<string, number>).startX * 2,
      } as unknown as Session)
    }
  }, [magnification, remainRightArea])

  useEffect(() => {
    // 세션셀렉터에서 선택한 경우임.
    if (selectedSession) {
      const copied = cloneDeep(selectedSession) as unknown as TimelineItemBoxProps
      copied.startY = 68
      copied.width = width
      copied.height = 37
      copied.startX = 0
      setCurrentItem(copied as Session)
    } else {
      setCurrentItem(undefined)
    }
  }, [selectedSession, width])

  useEffect(() => {
    if (currentItem) {
      const target = sessionList?.find((box) => box.idfSession === currentItem?.idfSession)

      if (target) {
        setSessionList(
          sessionList.map((session) => {
            if (session.idfSession === target.idfSession) {
              return currentItem
            }

            return session
          }),
        )
      }
    }
  }, [currentItem])

  useEffect(() => {
    setTimePerWidth(timePerWidth)
  }, [timePerWidth])

  useEffect(() => {
    setTimelineWidth(width)
  }, [width])

  return (
    <div className="SessionTimelineContWrapper" onScroll={handleScrollEvent} ref={svgContRef}>
      <div>
        <svg
          className={classNames("SessionTimelineCont")}
          width={width > 1 ? width + remainRightArea : "100%"}
          height={`${timelineHeight}px`}
          ref={ref}
          onMouseEnter={onMouseEnter}
          onMouseMove={onMouseMove}>
          <g>
            {currentItem && (
              <TimelineItemBox
                {...(currentItem as TimelineItemBoxProps)}
                className="session-box"
                fillColor={colors.primary[200]}
                strokeColor={colors.primary[500]}
                hoverable
                editable={false}
                rangeKey={rangeKey}
                remainRightArea={remainRightArea / 2}
                onClick={(item) => {
                  onBoxClick?.(item)
                }}
              />
            )}
          </g>
          <rect ref={interactiveRectRef} width="100%" height="100%" opacity="0" />

          <g>
            <TimelineTick
              width={width + remainRightArea}
              rangeKey={rangeKey}
              magnification={magnification}
              dateStart={tickStartDate}
              dateFinish={tickFinishDate}
            />
          </g>

          <g>
            <text x="-50" dx="100%" y="17" className="timezone-offset">
              {timezoneOffset}
            </text>
          </g>
        </svg>
      </div>
    </div>
  )
}
